/**
 * Modal de logs de execução de uma sessão específica do fluxo
 * Exibe timeline de steps executados com status, duração e dados de saída
 * Reutiliza o mesmo padrão visual do FlowExecutionHistory (StepItem)
 * 
 * @release v1.0.0 - 2026-03-07
 */

import { useQuery } from '@tanstack/react-query';
import {
  CheckCircle, XCircle, Play, Clock, Ban, AlertCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import type { Tables } from '@/integrations/supabase/types';

// =========================================================================
// Interfaces
// =========================================================================

interface FlowSessionLogsModalProps {
  /** ID da execução (session.id para sessões WhatsApp) */
  executionId: string | null;
  /** Controle de abertura do modal */
  open: boolean;
  /** Callback de mudança de estado do modal */
  onOpenChange: (open: boolean) => void;
}

// =========================================================================
// Configuração de status dos steps
// =========================================================================

/** Mapa de status para ícone e cor */
const STEP_STATUS_CONFIG: Record<string, { icon: typeof CheckCircle; color: string }> = {
  completed: { icon: CheckCircle, color: 'text-green-500' },
  failed: { icon: XCircle, color: 'text-red-500' },
  running: { icon: Play, color: 'text-blue-500' },
  pending: { icon: Clock, color: 'text-gray-400' },
  skipped: { icon: Ban, color: 'text-gray-400' },
  waiting: { icon: Clock, color: 'text-amber-500' },
};

/** Mapa de status da execução para badge */
const EXECUTION_STATUS: Record<string, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
  completed: { label: 'Concluído', variant: 'default' },
  running: { label: 'Em andamento', variant: 'secondary' },
  failed: { label: 'Falhou', variant: 'destructive' },
  paused: { label: 'Pausado', variant: 'outline' },
  cancelled: { label: 'Cancelado', variant: 'outline' },
  waiting_input: { label: 'Aguardando', variant: 'secondary' },
  waiting_delay: { label: 'Aguardando', variant: 'secondary' },
  in_progress: { label: 'Em andamento', variant: 'secondary' },
};

// =========================================================================
// Componente principal
// =========================================================================

export function FlowSessionLogsModal({ executionId, open, onOpenChange }: FlowSessionLogsModalProps) {
  // Busca dados básicos da execução (status, duração, trigger)
  const { data: execution, isLoading: loadingExecution } = useQuery({
    queryKey: ['flow-session-log-execution', executionId],
    queryFn: async () => {
      if (!executionId) return null;
      logger.info('[FlowSessionLogsModal] Buscando execução', executionId);

      const { data, error } = await supabase
        .from('flow_executions')
        .select('id, status, trigger_type, started_at, completed_at, current_node_id, context, flow_id')
        .eq('id', executionId)
        .maybeSingle();

      if (error) {
        logger.error('[FlowSessionLogsModal] Erro ao buscar execução', error.message);
        throw error;
      }
      return data;
    },
    enabled: !!executionId && open,
  });

  // Busca steps da execução
  const { data: steps = [], isLoading: loadingSteps } = useQuery({
    queryKey: ['flow-session-log-steps', executionId],
    queryFn: async () => {
      if (!executionId) return [];
      logger.info('[FlowSessionLogsModal] Buscando steps', executionId);

      const { data, error } = await supabase
        .from('flow_execution_steps')
        .select('*')
        .eq('execution_id', executionId)
        .order('started_at', { ascending: true });

      if (error) {
        logger.error('[FlowSessionLogsModal] Erro ao buscar steps', error.message);
        throw error;
      }
      logger.debug('[FlowSessionLogsModal] Steps carregados', String(data?.length || 0));
      return data || [];
    },
    enabled: !!executionId && open,
  });

  // Busca nodes do fluxo para resolver apelidos (nickname)
  const { data: nicknameMap } = useQuery({
    queryKey: ['flow-session-log-nicknames', execution?.flow_id],
    queryFn: async () => {
      if (!execution?.flow_id) return {};
      logger.info('[FlowSessionLogsModal] Buscando nodes do fluxo para nicknames', execution.flow_id);

      const { data, error } = await supabase
        .from('flows')
        .select('nodes')
        .eq('id', execution.flow_id)
        .maybeSingle();

      if (error) {
        logger.error('[FlowSessionLogsModal] Erro ao buscar nodes', error.message);
        return {};
      }

      // Constrói mapa nodeId → nickname com mesma prioridade da tabela de resultados
      const map: Record<string, string> = {};
      const nodes = (data?.nodes as Array<{ id: string; type?: string; data?: { label?: string; config?: { nickname?: string; question?: string } } }>) || [];
      for (const node of nodes) {
        map[node.id] = node.data?.config?.nickname || node.data?.label || node.data?.config?.question || node.type || node.id;
      }
      return map;
    },
    enabled: !!execution?.flow_id,
  });

  /** Calcula duração total da execução */
  const totalDuration = (() => {
    if (!execution?.started_at || !execution?.completed_at) return null;
    const diffMs = new Date(execution.completed_at).getTime() - new Date(execution.started_at).getTime();
    if (diffMs < 0) return null;
    const seconds = Math.floor(diffMs / 1000);
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hrs = Math.floor(mins / 60);
    return `${hrs}h ${mins % 60}m`;
  })();

  const isLoading = loadingExecution || loadingSteps;
  const statusCfg = execution ? EXECUTION_STATUS[execution.status] || { label: execution.status, variant: 'outline' as const } : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Logs da Execução
            {statusCfg && (
              <Badge variant={statusCfg.variant} className="text-xs">
                {statusCfg.label}
              </Badge>
            )}
            {totalDuration && (
              <span className="text-xs text-muted-foreground font-normal">
                ({totalDuration})
              </span>
            )}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Timeline detalhada dos passos executados nesta sessão
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-2">
          {/* Estado de carregamento */}
          {isLoading && (
            <div className="space-y-2 py-4">
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
              <Skeleton className="h-14 w-full" />
            </div>
          )}

          {/* Sem steps */}
          {!isLoading && steps.length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-8">
              Nenhum passo de execução registrado para esta sessão
            </div>
          )}

          {/* Lista de steps */}
          {!isLoading && steps.length > 0 && (
            <div className="space-y-2 py-2">
              {steps.map((step, index) => (
                <StepLogItem key={step.id} step={step} index={index} nicknameMap={nicknameMap || {}} />
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

// =========================================================================
// Componente de item de step individual
// =========================================================================

interface StepLogItemProps {
  step: Tables<'flow_execution_steps'>;
  index: number;
  /** Mapa de nodeId → apelido (nickname) para exibição amigável */
  nicknameMap: Record<string, string>;
}

/** Item individual de step na timeline (mesmo visual do FlowExecutionHistory) */
function StepLogItem({ step, index, nicknameMap }: StepLogItemProps) {
  const config = STEP_STATUS_CONFIG[step.status] || { icon: AlertCircle, color: 'text-gray-500' };
  // Resolve nome amigável do node: nickname > node_type
  const displayName = nicknameMap[step.node_id] || step.node_type;
  const Icon = config.icon;

  // Calcula duração do step
  const duration =
    step.completed_at && step.started_at
      ? Math.round(
          (new Date(step.completed_at).getTime() -
            new Date(step.started_at).getTime()) /
            1000
        )
      : null;

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/50 border">
      {/* Número do step */}
      <div className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-semibold">
        {index + 1}
      </div>

      {/* Conteúdo do step */}
      <div className="flex-1 min-w-0">
        {/* Cabeçalho: ícone + tipo + duração */}
        <div className="flex items-center gap-2 mb-1">
          <Icon className={`h-4 w-4 ${config.color}`} />
          <span className="font-medium text-sm">{displayName}</span>
          {duration !== null && (
            <span className="text-xs text-muted-foreground">({duration}s)</span>
          )}
        </div>

        {/* Tipo técnico do node (referência) */}
        {displayName !== step.node_type && (
          <div className="text-xs text-muted-foreground/70 italic mb-1">
            Tipo: {step.node_type}
          </div>
        )}

        {/* ID do nó */}
        <div className="text-xs text-muted-foreground mb-2">
          ID do Nó: {step.node_id}
        </div>

        {/* Mensagem de erro */}
        {step.error_message && (
          <div className="text-xs text-destructive bg-destructive/10 p-2 rounded mt-2">
            ⚠️ {step.error_message}
          </div>
        )}

        {/* Dados de saída expansíveis */}
        {step.output_data && (
          <details className="mt-2">
            <summary className="text-xs cursor-pointer text-primary hover:underline">
              Ver dados de saída
            </summary>
            <pre className="text-xs bg-background p-2 rounded mt-1 overflow-x-auto">
              {JSON.stringify(step.output_data, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}
